import { Component, OnInit } from '@angular/core';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import {
  formataDuracao,
  UsoClienteResumo,
  UsoDia,
  UsoRotina,
  UsoSistemaPainel,
  UsoSistemaService,
  UsoUsuarioResumo,
} from 'src/app/services/uso-sistema.service';

interface Grafico {
  dados: any;
  opcoes: any;
}

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const TOP_ROTINAS_GRAFICO = 10;
/** Valor de "Todas as empresas" no combo: o dropdown não mostra o rótulo de uma opção com valor null. */
const TODAS_EMPRESAS = -1;

/**
 * Painel do uso do sistema enviado diariamente pelos clientes (rotinas, logins e IA da Haila).
 * Três níveis na mesma tela: geral → cliente → usuário. Os dados chegam até ontem. No cliente e no
 * usuário dá para olhar uma empresa só do tenant.
 */
@Component({
  selector: 'habil-uso-sistema',
  templateUrl: './uso-sistema.component.html',
  styleUrls: ['./uso-sistema.component.scss'],
})
export class UsoSistemaComponent implements OnInit {
  duracao = formataDuracao;

  periodos = [
    { label: 'Últimos 7 dias', value: 7 },
    { label: 'Últimos 30 dias', value: 30 },
    { label: 'Últimos 90 dias', value: 90 },
    { label: 'Últimos 12 meses', value: 365 },
    { label: 'Personalizado', value: 0 },
  ];
  periodo = 30;
  inicio = '';
  fim = '';

  cnpj: string = null;
  nomeCliente = '';
  idUsuario: number = null;
  nomeUsuario = '';
  empresa = TODAS_EMPRESAS;
  opcoesEmpresa: { label: string; value: number }[] = [];

  carregando = false;
  erro = '';
  painel: UsoSistemaPainel = null;

  buscaLista = '';
  clientes: UsoClienteResumo[] = [];
  usuarios: UsoUsuarioResumo[] = [];
  buscaRotina = '';
  categoriaRotina = '';
  categoriasRotina: { label: string; value: string }[] = [];
  rotinas: UsoRotina[] = [];

  tempoMedio = 0;
  diasPeriodo = 0;
  graficoDiario: Grafico = null;
  graficoRotinas: Grafico = null;
  graficoCategorias: Grafico = null;
  graficoSemana: Grafico = null;
  graficoOrigens: Grafico = null;
  graficoIa: Grafico = null;

  constructor(private service: UsoSistemaService, breadcrumbService: BreadcrumbService) {
    breadcrumbService.setItems([{ label: 'Administração' }, { label: 'Uso do Sistema' }]);
  }

  ngOnInit(): void {
    this.aplicaPeriodo();
  }

  get nivel(): 'geral' | 'cliente' | 'usuario' {
    return this.idUsuario !== null ? 'usuario' : this.cnpj ? 'cliente' : 'geral';
  }

  aplicaPeriodo() {
    if (!this.periodo) return;

    const ontem = new Date();
    ontem.setDate(ontem.getDate() - 1);
    const inicio = new Date(ontem);
    inicio.setDate(ontem.getDate() - (this.periodo - 1));
    this.fim = UsoSistemaComponent.iso(ontem);
    this.inicio = UsoSistemaComponent.iso(inicio);
    this.carregar();
  }

  mudouData() {
    this.periodo = 0;
  }

  carregar() {
    if (!this.inicio || !this.fim || this.inicio > this.fim) {
      this.limpa();
      this.erro = 'Informe um período válido.';
      return;
    }
    this.carregando = true;
    this.erro = '';
    const empresa = this.cnpj && this.empresa !== TODAS_EMPRESAS ? this.empresa : null;
    this.service.painel(this.inicio, this.fim, this.cnpj, this.idUsuario, empresa).subscribe({
      next: (p) => {
        this.carregando = false;
        this.monta(p);
      },
      error: (e) => {
        this.carregando = false;
        this.limpa();
        this.erro = typeof e?.error === 'string' && e.error ? e.error : 'Não foi possível consultar o uso do sistema.';
      },
    });
  }

  irParaGeral() {
    this.cnpj = null;
    this.nomeCliente = '';
    this.limpaEmpresa();
    this.irParaCliente();
  }

  irParaCliente() {
    this.idUsuario = null;
    this.nomeUsuario = '';
    this.buscaLista = '';
    this.carregar();
  }

  abrirCliente(c: UsoClienteResumo) {
    this.cnpj = c.cnpj;
    this.nomeCliente = c.nome || this.documento(c.cnpj);
    this.limpaEmpresa();
    this.irParaCliente();
  }

  abrirUsuario(u: UsoUsuarioResumo) {
    this.idUsuario = u.idUsuario;
    this.nomeUsuario = u.nome || 'Usuário ' + u.idUsuario;
    this.buscaLista = '';
    this.carregar();
  }

  filtrarLista() {
    const texto = (this.buscaLista || '').trim().toLowerCase();
    const digitos = texto.replace(/\D/g, '');
    this.clientes = (this.painel?.clientes || []).filter(
      (c) => !texto || (c.nome || '').toLowerCase().includes(texto) || (digitos.length >= 3 && c.cnpj.includes(digitos))
    );
    this.usuarios = (this.painel?.usuarios || []).filter((u) => !texto || (u.nome || '').toLowerCase().includes(texto));
  }

  filtrarRotinas() {
    const texto = (this.buscaRotina || '').trim().toLowerCase();
    this.rotinas = (this.painel?.rotinas || []).filter(
      (r) =>
        (!this.categoriaRotina || r.categoria === this.categoriaRotina) &&
        (!texto || (r.descricao || r.rotina || '').toLowerCase().includes(texto))
    );
  }

  private limpaEmpresa() {
    this.empresa = TODAS_EMPRESAS;
    this.opcoesEmpresa = [];
  }

  /** Sem resposta para o filtro atual, nada do filtro anterior pode continuar na tela. */
  private limpa() {
    this.painel = null;
    this.clientes = [];
    this.usuarios = [];
    this.rotinas = [];
  }

  private monta(p: UsoSistemaPainel) {
    this.painel = p;
    if (p.nomeCliente) {
      this.nomeCliente = p.nomeCliente;
    }
    if (p.nomeUsuario) {
      this.nomeUsuario = p.nomeUsuario;
    }
    this.opcoesEmpresa = [{ label: 'Todas as empresas', value: TODAS_EMPRESAS }].concat(
      (p.empresas || []).map((e) => ({
        label: (e.nome || 'Empresa ' + e.idEmpresa) + (e.cnpj ? ' · ' + this.documento(e.cnpj) : ''),
        value: e.idEmpresa,
      }))
    );
    this.categoriasRotina = [{ label: 'Todas', value: '' }].concat(
      (p.categorias || []).map((c) => ({ label: c.categoria, value: c.categoria }))
    );
    if (!this.categoriasRotina.some((c) => c.value === this.categoriaRotina)) {
      this.categoriaRotina = '';
    }
    this.filtrarLista();
    this.filtrarRotinas();

    const dias = this.diasCompletos(p);
    this.diasPeriodo = dias.length;
    const usuariosDia = dias.reduce((s, d) => s + d.usuarios, 0);
    this.tempoMedio = usuariosDia > 0 ? p.totais.tempo / usuariosDia : 0;

    const c = UsoSistemaComponent.cores();
    this.graficoDiario = this.montaDiario(dias, c);
    this.graficoRotinas = this.montaRotinas(p.rotinas, c);
    this.graficoCategorias = this.montaPizza(
      (p.categorias || []).map((x) => x.categoria),
      (p.categorias || []).map((x) => UsoSistemaComponent.horas(x.tempo)),
      c,
      (v) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' h'
    );
    this.graficoSemana = this.montaSemana(dias, c);
    this.graficoOrigens =
      this.nivel === 'geral' && (p.origens || []).length
        ? this.montaPizza(
            p.origens.map((o) => o.origem),
            p.origens.map((o) => o.empresas),
            c,
            (v) => v.toLocaleString('pt-BR') + ' clientes'
          )
        : null;
    this.graficoIa = p.totais.iaChamadas > 0 ? this.montaIa(dias, c) : null;
  }

  /** Um item por dia do período, com zero onde não houve uso, para os gráficos não pularem dias. */
  private diasCompletos(p: UsoSistemaPainel): UsoDia[] {
    const porData = new Map((p.dias || []).map((d) => [d.data, d] as [string, UsoDia]));
    const lista: UsoDia[] = [];
    for (const d = UsoSistemaComponent.data(p.inicio); UsoSistemaComponent.iso(d) <= p.fim; d.setDate(d.getDate() + 1)) {
      const data = UsoSistemaComponent.iso(d);
      lista.push(porData.get(data) || { data, usuarios: 0, logins: 0, entradas: 0, tempo: 0, iaChamadas: 0 });
    }
    return lista;
  }

  private montaDiario(dias: UsoDia[], c: Cores): Grafico {
    return {
      dados: {
        labels: dias.map((d) => UsoSistemaComponent.rotuloDia(d.data)),
        datasets: [
          {
            type: 'line',
            label: this.nivel === 'usuario' ? 'Logins' : 'Usuários ativos',
            data: dias.map((d) => (this.nivel === 'usuario' ? d.logins : d.usuarios)),
            borderColor: c.laranja,
            backgroundColor: c.laranja,
            pointRadius: dias.length > 60 ? 0 : 2,
            tension: 0.3,
            yAxisID: 'usuarios',
          },
          {
            type: 'bar',
            label: 'Horas em rotinas',
            data: dias.map((d) => UsoSistemaComponent.horas(d.tempo)),
            backgroundColor: c.azulSuave,
            borderColor: c.azul,
            borderWidth: 1,
            yAxisID: 'horas',
          },
        ],
      },
      opcoes: {
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: this.legenda(c, 'bottom') },
        scales: {
          x: this.eixo(c, { autoSkip: true, maxRotation: 0 }, false),
          horas: { ...this.eixo(c, { callback: (v: any) => v + 'h' }), position: 'left' },
          usuarios: {
            ...this.eixo(c, { precision: 0 }),
            position: 'right',
            grid: { drawOnChartArea: false },
            title: { display: true, text: this.nivel === 'usuario' ? 'Logins' : 'Usuários', color: c.apagado },
          },
        },
      },
    };
  }

  private montaRotinas(rotinas: UsoRotina[], c: Cores): Grafico {
    const top = (rotinas || []).slice(0, TOP_ROTINAS_GRAFICO);
    return {
      dados: {
        labels: top.map((r) => UsoSistemaComponent.corta(r.descricao || r.rotina, 32)),
        datasets: [
          {
            label: 'Horas',
            data: top.map((r) => UsoSistemaComponent.horas(r.tempo)),
            backgroundColor: top.map((r) => (r.categoria === 'Relatório' ? c.roxo : c.azul)),
          },
        ],
      },
      opcoes: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (itens: any[]) => (top[itens[0].dataIndex]?.descricao || top[itens[0].dataIndex]?.rotina),
              label: (item: any) => {
                const r = top[item.dataIndex];
                return `${formataDuracao(r.tempo)} · ${r.entradas.toLocaleString('pt-BR')} entradas · ${r.usuarios.toLocaleString('pt-BR')} usuários`;
              },
            },
          },
        },
        scales: { x: this.eixo(c, { callback: (v: any) => v + 'h' }), y: this.eixo(c, {}, false) },
      },
    };
  }

  /** Média, em cada dia da semana do período, de usuários ativos (ou de horas, na visão de um usuário). */
  private montaSemana(dias: UsoDia[], c: Cores): Grafico {
    const usuario = this.nivel === 'usuario';
    const soma = [0, 0, 0, 0, 0, 0, 0];
    const qtde = [0, 0, 0, 0, 0, 0, 0];
    dias.forEach((d) => {
      const dia = UsoSistemaComponent.data(d.data).getDay();
      soma[dia] += usuario ? UsoSistemaComponent.horas(d.tempo) : d.usuarios;
      qtde[dia]++;
    });
    return {
      dados: {
        labels: DIAS_SEMANA,
        datasets: [
          {
            label: usuario ? 'Média de horas em rotinas' : 'Média de usuários ativos',
            data: soma.map((s, i) => (qtde[i] ? Math.round((s / qtde[i]) * 10) / 10 : 0)),
            backgroundColor: c.verde,
          },
        ],
      },
      opcoes: {
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: this.eixo(c, {}, false), y: this.eixo(c, {}) },
      },
    };
  }

  private montaIa(dias: UsoDia[], c: Cores): Grafico {
    return {
      dados: {
        labels: dias.map((d) => UsoSistemaComponent.rotuloDia(d.data)),
        datasets: [
          {
            label: 'Chamadas à Haila',
            data: dias.map((d) => d.iaChamadas),
            borderColor: c.roxo,
            backgroundColor: c.roxoSuave,
            fill: true,
            pointRadius: dias.length > 60 ? 0 : 2,
            tension: 0.3,
          },
        ],
      },
      opcoes: {
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: this.eixo(c, { autoSkip: true, maxRotation: 0 }, false), y: this.eixo(c, { precision: 0 }) },
      },
    };
  }

  private montaPizza(rotulos: string[], valores: number[], c: Cores, formata: (v: number) => string): Grafico {
    const paleta = [c.azul, c.laranja, c.verde, c.roxo, c.amarelo, c.vermelho];
    return {
      dados: {
        labels: rotulos,
        datasets: [{ data: valores, backgroundColor: rotulos.map((_, i) => paleta[i % paleta.length]), borderColor: c.cartao }],
      },
      opcoes: {
        maintainAspectRatio: false,
        cutout: '58%',
        plugins: {
          legend: this.legenda(c, 'right'),
          tooltip: { callbacks: { label: (item: any) => `${item.label}: ${formata(Number(item.raw) || 0)}` } },
        },
      },
    };
  }

  private legenda(c: Cores, position: string) {
    return { display: true, position, labels: { boxWidth: 12, font: { size: 11 }, color: c.texto } };
  }

  private eixo(c: Cores, ticks: any, grade = true) {
    return {
      grid: { color: c.grade, drawBorder: false, display: grade },
      ticks: { color: c.apagado, font: { size: 11 }, ...ticks },
      beginAtZero: true,
    };
  }

  private static cores(): Cores {
    const estilo = getComputedStyle(document.documentElement);
    const v = (nome: string, padrao: string) => (estilo.getPropertyValue(nome) || '').trim() || padrao;
    return {
      azul: v('--cc-azul', '#1f6fa8'),
      azulSuave: v('--cc-azul-suave', '#d9ecf7'),
      laranja: v('--cc-laranja-texto', '#b45309'),
      verde: v('--cc-verde', '#2e7d4f'),
      roxo: v('--cc-roxo', '#6a3fb5'),
      roxoSuave: v('--cc-roxo-suave', '#ece4f8'),
      amarelo: v('--cc-amarelo', '#c99a06'),
      vermelho: v('--cc-vermelho', '#8a1f1f'),
      texto: v('--cc-texto', '#495057'),
      apagado: v('--cc-apagado', '#5f676f'),
      grade: v('--cc-borda', '#eef0f2'),
      cartao: v('--cc-cartao', '#ffffff'),
    };
  }

  documento(cnpj: string): string {
    const d = (cnpj || '').replace(/\D/g, '');
    if (d.length === 14) {
      return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    if (d.length === 11) {
      return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    return cnpj;
  }

  private static horas(segundos: number): number {
    return Math.round(((Number(segundos) || 0) / 3600) * 10) / 10;
  }

  private static corta(texto: string, tamanho: number): string {
    return texto && texto.length > tamanho ? texto.substring(0, tamanho - 1) + '…' : texto;
  }

  private static rotuloDia(iso: string): string {
    return iso.substring(8, 10) + '/' + iso.substring(5, 7);
  }

  private static data(iso: string): Date {
    const [a, m, d] = iso.split('-').map(Number);
    return new Date(a, m - 1, d);
  }

  private static iso(d: Date): string {
    const doisDigitos = (n: number) => (n < 10 ? '0' : '') + n;
    return d.getFullYear() + '-' + doisDigitos(d.getMonth() + 1) + '-' + doisDigitos(d.getDate());
  }
}

interface Cores {
  azul: string;
  azulSuave: string;
  laranja: string;
  verde: string;
  roxo: string;
  roxoSuave: string;
  amarelo: string;
  vermelho: string;
  texto: string;
  apagado: string;
  grade: string;
  cartao: string;
}
